import { useState, useEffect, useCallback } from 'react';
import { impersonationApi } from '../services/api';
import { useAuth } from './useAuth';

export interface ImpersonationState {
  associateid: number;
  name: string;
}

// #2116 — module-level verify cache. PageHeader mounts on every navigation
// and several components use this hook at once, which used to fire 2-4
// copies of /api/auth/impersonate/list/ PER PAGE CHANGE (1.5-2.9s each on
// field links — diag #51 actionLog). Superuser-only path (#1949 already
// gates reps out): verify once, share the in-flight promise across hook
// instances, reuse the result for the TTL. Impersonation set/clear both
// hard-reload the app (Profile.tsx / clearImpersonation), which resets this
// module state, so a stale cache can't outlive an identity switch — the
// invalidate call in clearImpersonation is belt-and-braces.
const VERIFY_TTL_MS = 5 * 60 * 1000;
let verifyCache: Promise<ImpersonationState | null> | null = null;
let verifiedAt = 0;

function verifyWithServer(): Promise<ImpersonationState | null> {
  if (verifyCache && Date.now() - verifiedAt < VERIFY_TTL_MS) {
    return verifyCache;
  }
  verifiedAt = Date.now();
  const request = impersonationApi
    .getList()
    .then((response) => response.current_impersonation ?? null);
  // A failed verify must not be cached for the TTL — drop it so the next
  // mount retries.
  request.catch(() => {
    if (verifyCache === request) verifyCache = null;
  });
  verifyCache = request;
  return request;
}

export function invalidateImpersonationVerify(): void {
  verifyCache = null;
  verifiedAt = 0;
}

export function useImpersonation() {
  const { user } = useAuth();
  const [impersonating, setImpersonating] = useState<ImpersonationState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load from localStorage first, then verify with server
  useEffect(() => {
    // First, load from localStorage for immediate display
    const stored = localStorage.getItem('impersonating');
    let localData: ImpersonationState | null = null;
    if (stored) {
      try {
        localData = JSON.parse(stored);
        // Only use if it has both associateid and name
        if (localData && localData.associateid && localData.name) {
          setImpersonating(localData);
        }
      } catch {
        localData = null;
      }
    }

    // Non-superusers can never have an active impersonation server-side, so
    // skip the verifying round-trip entirely. PageHeader mounts on every
    // navigation, so for regular reps this was firing a guaranteed-403
    // request to /api/auth/impersonate/list/ on every page change (#1949).
    if (user && !user.is_superuser) {
      // If somehow stale localStorage said we were impersonating, clear it.
      if (localData) {
        setImpersonating(null);
        localStorage.removeItem('impersonating');
      }
      setIsLoading(false);
      return;
    }
    // If auth is still bootstrapping (user not yet resolved), defer the
    // server sync until we know whether to call it at all. The effect will
    // re-run when `user` populates.
    if (!user) {
      return;
    }

    // Then verify with server to sync state (handles session changes, missing
    // name, etc.). Cached + single-flighted across hook instances (#2116).
    async function syncWithServer() {
      try {
        const serverState = await verifyWithServer();
        if (serverState) {
          // Server says we're impersonating - update state and localStorage
          setImpersonating(serverState);
          localStorage.setItem('impersonating', JSON.stringify(serverState));
        } else if (localData) {
          // Server says no impersonation but we had local data - clear it
          setImpersonating(null);
          localStorage.removeItem('impersonating');
        }
      } catch {
        // API call failed (user not superuser or network error)
        // Keep localStorage state as-is if it has valid data
        if (!localData || !localData.name) {
          setImpersonating(null);
          localStorage.removeItem('impersonating');
        }
      } finally {
        setIsLoading(false);
      }
    }

    syncWithServer();
  }, [user]);

  // Listen for changes to localStorage (from other components/tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'impersonating') {
        if (e.newValue) {
          try {
            setImpersonating(JSON.parse(e.newValue));
          } catch {
            setImpersonating(null);
          }
        } else {
          setImpersonating(null);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Clear impersonation
  const clearImpersonation = useCallback(async () => {
    try {
      const response = await impersonationApi.clearImpersonation();
      invalidateImpersonationVerify();
      localStorage.removeItem('impersonating');
      // Refresh the cached associate to the real user's associate so subsequent
      // API calls carry the correct associate id. Without this the PWA would
      // keep sending the previously impersonated rep's id and writes would
      // continue to credit the wrong associate (#1499).
      if (response?.associate) {
        localStorage.setItem('associate', JSON.stringify(response.associate));
      } else {
        // Backend didn't return one — drop the stale cache so the next API
        // call fails loudly rather than silently writing under the old id.
        localStorage.removeItem('associate');
      }
      setImpersonating(null);
      // Reload to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('[useImpersonation] Failed to clear:', error);
    }
  }, []);

  return {
    impersonating,
    isLoading,
    isImpersonating: !!impersonating,
    clearImpersonation,
  };
}
